import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../home/components/Footer';

export default function CreateRound() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const requestedRound = searchParams.get('round') || 'Round 1';
  
  const [formData, setFormData] = useState({
    founder_name: '',
    company_name: '',
    app_name: '',
    platform: '',
    company_stage: '',
    preferred_round: requestedRound,
    test_tier: '',
    description: '',
    mau: '',
    revenue: '',
    website: '',
    contact_email: '',
    contact_phone: '',
    nda_required: false,
    terms_consent: false,
    utm_source: searchParams.get('utm_source') || ''
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [round1Completed, setRound1Completed] = useState(false);

  // Round progression enforcement - check on mount and when round changes
  useEffect(() => {
    const checkRound1Status = () => {
      const r1Complete = localStorage.getItem('round_1_completed') === 'true';
      setRound1Completed(r1Complete);
      
      if (formData.preferred_round !== 'Round 1' && !r1Complete) {
        setError(`⚠️ Round 1 baseline is required before starting ${formData.preferred_round}. Please complete Round 1 first.`);
      } else {
        setError('');
      }
    };
    
    checkRound1Status();
  }, [formData.preferred_round]);

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validation
      if (!validateEmail(formData.contact_email)) {
        throw new Error('Please enter a valid email address');
      }

      if (formData.description.length > 1000) {
        throw new Error('Description must be 1000 characters or less');
      }

      if (!formData.terms_consent) {
        throw new Error('You must agree to the Terms of Service and Privacy Policy');
      }

      // SERVER-SIDE ENFORCEMENT: Round 1 must be completed before Round 2/3
      if (formData.preferred_round !== 'Round 1') {
        const round1Completed = localStorage.getItem('round_1_completed') === 'true';
        if (!round1Completed) {
          throw new Error('❌ Round 1 baseline is required. You must complete Round 1 before starting Round 2 or Round 3. Please select Round 1 to begin your validation journey.');
        }
      }

      // Prepare webhook payload matching Gravity Forms structure
      const webhookPayload = {
        timestamp: new Date().toLocaleDateString('en-US'),
        founder_name: formData.founder_name,
        company_name: formData.company_name,
        app_name: formData.app_name,
        platform: formData.platform,
        company_stage: formData.company_stage,
        preferred_round: formData.preferred_round,
        test_tier: formData.test_tier,
        description: formData.description,
        mau: formData.mau ? parseInt(formData.mau) : null,
        revenue: formData.revenue,
        website: formData.website,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        nda_required: formData.nda_required,
        utm_source: formData.utm_source,
        consent: formData.terms_consent,
        request_id: crypto.randomUUID()
      };

      // Submit to Supabase Edge Function webhook
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/submit-round1-application`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Request-ID': webhookPayload.request_id
        },
        body: JSON.stringify(webhookPayload)
      });

      const result = await response.json();

      if (!response.ok || result.status === 'error') {
        throw new Error(result.message || 'Submission failed. Please try again or contact support.');
      }

      // Success - use project_id from backend
      setProjectId(result.project_id || `CR-${Date.now()}`);
      setShowSuccess(true);
      
      // Mark round as started in localStorage (for demo purposes)
      if (formData.preferred_round === 'Round 1') {
        localStorage.setItem('round_1_started', new Date().toISOString());
        // Note: round_1_completed should be set by ops team after actual completion
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed. Please try again or contact support@crowdrounds.com');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Start Your First Test
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Begin your validation journey with baseline testing and establish your CrownScore
          </p>
        </div>
      </section>

      {/* Estimate Banner */}
      <section className="py-4 bg-blue-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-sm text-blue-800">
            <i className="ri-time-line mr-2"></i>
            <strong>Typical turnaround:</strong> Round 1 projects typically return results in 7–14 days
          </p>
        </div>
      </section>

      {/* Round Progression Info Banner */}
      {!round1Completed && (
        <section className="py-4 bg-amber-50 border-b border-amber-200">
          <div className="max-w-4xl mx-auto px-6">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-xl text-amber-700 mt-0.5"></i>
              <div className="text-sm text-amber-900">
                <strong>Round Progression:</strong> All projects must start with Round 1 to establish your baseline CrownScore. 
                Round 2 and Round 3 unlock after Round 1 completion (typically 7-14 days).
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Error Message */}
      {error && (
        <section className="py-8">
          <div className="max-w-4xl mx-auto px-6">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <i className="ri-error-warning-line text-2xl text-red-600 flex-shrink-0"></i>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-red-900 mb-2">Cannot Start {formData.preferred_round}</h3>
                  <p className="text-red-800 mb-4">{error}</p>
                  {formData.preferred_round !== 'Round 1' && (
                    <button
                      onClick={() => setFormData(prev => ({ ...prev, preferred_round: 'Round 1' }))}
                      className="px-6 py-2.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      ✓ Select Round 1 to Start
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Success Message */}
      {showSuccess && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-xl max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">✓ Test Request Submitted!</h3>
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <div className="text-sm text-gray-600 mb-1">Your Project ID</div>
                <div className="text-2xl font-bold text-blue-600">{projectId}</div>
              </div>
              <p className="text-gray-600 mb-6">
                Thanks, <strong>{formData.founder_name}</strong> — your test request for <strong>{formData.app_name}</strong> is confirmed.
              </p>
              <div className="text-left bg-gray-50 rounded-lg p-6 mb-6">
                <h4 className="font-bold mb-3">What happens next:</h4>
                <ol className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">1</span>
                    <span><strong>Admin review (24–48h)</strong> — we verify your submission and confirm availability</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">2</span>
                    <span><strong>Ops sends quote</strong> — scoped pricing + onboarding checklist</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">3</span>
                    <span><strong>Schedule {formData.preferred_round}</strong> — we begin tester recruitment and onboarding</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">4</span>
                    <span><strong>Results delivered</strong> — comprehensive report with CrownScore in 7-14 days</span>
                  </li>
                </ol>
              </div>
              <p className="text-gray-600 mb-6">
                We'll email scheduling details to <strong>{formData.contact_email}</strong> shortly.
              </p>
              <div className="flex gap-3 justify-center flex-wrap">
                <button
                  onClick={() => navigate('/client-portal')}
                  className="px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Go to Client Portal
                </button>
                <button
                  onClick={() => {
                    setShowSuccess(false);
                    setFormData({
                      founder_name: '',
                      company_name: '',
                      app_name: '',
                      platform: '',
                      company_stage: '',
                      preferred_round: 'Round 1',
                      test_tier: '',
                      description: '',
                      mau: '',
                      revenue: '',
                      website: '',
                      contact_email: '',
                      contact_phone: '',
                      nda_required: false,
                      terms_consent: false,
                      utm_source: ''
                    });
                  }}
                  className="px-8 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Submit Another
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Application Form */}
      {!showSuccess && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <form onSubmit={handleSubmit} data-readdy-form className="bg-white border border-gray-200 rounded-xl p-8 shadow-lg">
              <h2 className="text-2xl font-bold mb-2">Start First Test / Apply</h2>
              <p className="text-gray-600 mb-8">Complete this form to begin your validation journey with CrowdRounds.</p>
              
              {/* Founder & Company Info */}
              <div className="mb-8">
                <h3 className="text-lg font-bold mb-4 text-gray-700">Founder & Company Information</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Founder Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="founder_name"
                      value={formData.founder_name}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="Jane Doe"
                      required
                      aria-label="Founder Full Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Company / Legal Name
                    </label>
                    <input
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="Acme Apps LLC"
                      aria-label="Company Name"
                    />
                  </div>
                </div>
              </div>

              {/* App Details */}
              <div className="mb-8">
                <h3 className="text-lg font-bold mb-4 text-gray-700">App Details</h3>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      App Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="app_name"
                      value={formData.app_name}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="AcmeVPN"
                      required
                      aria-label="App Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Platform <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="platform"
                      value={formData.platform}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg text-sm"
                      aria-label="Platform"
                    >
                      <option value="">Select platform...</option>
                      <option value="iOS">iOS</option>
                      <option value="Android">Android</option>
                      <option value="Web">Web</option>
                      <option value="Cross-platform">Cross-platform</option>
                    </select>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold mb-2">
                    Company Stage
                  </label>
                  <select
                    name="company_stage"
                    value={formData.company_stage}
                    onChange={handleChange}
                    className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg text-sm"
                    aria-label="Company Stage"
                  >
                    <option value="">Select stage...</option>
                    <option value="Pre-seed">Pre-seed</option>
                    <option value="Beta">Beta</option>
                    <option value="Launched">Launched</option>
                    <option value="Revenue">Revenue</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold mb-2">
                    Brief App Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg h-32 text-sm"
                    placeholder="High-speed VPN for privacy-conscious users in MENA. Key features to test: connection speed, server switching, kill switch reliability..."
                    required
                    maxLength={1000}
                    aria-label="Brief App Description"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.description.length}/1000 characters</p>
                </div>
              </div>

              {/* Testing Preferences */}
              <div className="mb-8">
                <h3 className="text-lg font-bold mb-4 text-gray-700">Testing Preferences</h3>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Desired Start Round <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="preferred_round"
                      value={formData.preferred_round}
                      onChange={handleChange}
                      required
                      disabled={!round1Completed && formData.preferred_round !== 'Round 1'}
                      className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                      aria-label="Desired Start Round"
                    >
                      <option value="Round 1">Round 1 (required to start)</option>
                      <option value="Round 2" disabled={!round1Completed}>
                        Round 2 {!round1Completed ? '(locked - complete R1 first)' : ''}
                      </option>
                      <option value="Round 3" disabled={!round1Completed}>
                        Round 3 {!round1Completed ? '(locked - complete R1 first)' : ''}
                      </option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      <i className="ri-information-line"></i> Round 1 must be completed before Round 2/3
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Desired Test Size <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="test_tier"
                      value={formData.test_tier}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg text-sm"
                      aria-label="Desired Test Size"
                    >
                      <option value="">Choose one...</option>
                      <option value="50 — Starter">50 — Starter</option>
                      <option value="100 — Certified">100 — Certified</option>
                      <option value="200 — Validated">200 — Validated</option>
                      <option value="500+ — Premium">500+ — Premium</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Business Metrics */}
              <div className="mb-8">
                <h3 className="text-lg font-bold mb-4 text-gray-700">Business Metrics</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Monthly Active Users (MAU)
                    </label>
                    <input
                      type="number"
                      name="mau"
                      value={formData.mau}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="e.g., 12000"
                      aria-label="Monthly Active Users"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      <i className="ri-question-line"></i> Why we ask: helps us calibrate test scenarios to your scale
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Revenue / Monetization
                    </label>
                    <input
                      type="text"
                      name="revenue"
                      value={formData.revenue}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="e.g., $5k MRR, freemium, ads"
                      aria-label="Revenue / Monetization"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      <i className="ri-question-line"></i> Why we ask: helps us understand your business model and test payment flows
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="mb-8">
                <h3 className="text-lg font-bold mb-4 text-gray-700">Contact Information</h3>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Company Website
                    </label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="https://acme.example"
                      aria-label="Company Website"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Contact Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="contact_email"
                      value={formData.contact_email}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="jane@acme.com"
                      required
                      aria-label="Contact Email"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Contact Phone</label>
                    <input
                      type="tel"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="+1-555-555-1212"
                      aria-label="Contact Phone"
                    />
                  </div>
                </div>
              </div>

              {/* NDA & Terms */}
              <div className="mb-8">
                <label className="flex items-start p-4 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    name="nda_required"
                    checked={formData.nda_required}
                    onChange={handleChange}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-600 mt-0.5 cursor-pointer"
                    aria-label="NDA Required"
                  />
                  <span className="ml-3 text-sm text-gray-700">
                    <strong>NDA Required:</strong> I require testers to sign a Non-Disclosure Agreement
                  </span>
                </label>

                <label className="flex items-start p-4 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    name="terms_consent"
                    checked={formData.terms_consent}
                    onChange={handleChange}
                    required
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-600 mt-0.5 cursor-pointer"
                    aria-label="Terms Consent"
                  />
                  <span className="ml-3 text-sm text-gray-700">
                    <span className="text-red-500">*</span> I consent & accept{' '}
                    <a href="/terms" className="text-blue-600 hover:underline font-semibold" target="_blank">
                      Terms of Service
                    </a>
                    {' '}and{' '}
                    <a href="/privacy" className="text-blue-600 hover:underline font-semibold" target="_blank">
                      Privacy Policy
                    </a>
                  </span>
                </label>
              </div>

              {/* Honeypot / Anti-spam */}
              <div className="hidden" aria-hidden="true">
                <label>Leave this field empty</label>
                <input type="text" name="honeypot" tabIndex={-1} autoComplete="off" />
              </div>

              <button
                type="submit"
                disabled={loading || (formData.preferred_round !== 'Round 1' && !round1Completed)}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                title={formData.preferred_round !== 'Round 1' && !round1Completed ? 'Complete Round 1 to unlock' : ''}
              >
                {loading ? 'Submitting...' : 'Submit Test Request'}
              </button>
            </form>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
